import {
  SpainProvinceCode,
  toSpainProvinceCode,
} from './spain-provinces'

/**
 * Región fiscal a efectos del desglose del checkout y de la factura de Holded.
 *
 * El importe FINAL que paga el alumno es el MISMO en las dos regiones: lo que
 * cambia es el reparto entre base imponible e impuesto. Un plan de 590 € son
 * 590 € en Madrid y en Tenerife.
 *
 *   peninsula -> IVA 21%  (base = total / 1.21)
 *   canarias  -> IGIC 7%  (base = total / 1.07)
 *
 * Ceuta (CE) y Melilla (ML) tienen IPSI y quedan FUERA DE ALCANCE: van al 21%.
 *
 * OJO: el literal de `TaxRegion` se escribe en la metadata del customer de
 * Stripe. Cambiarlo más adelante obliga a migrar clientes de producción.
 */
export type TaxRegion = 'peninsula' | 'canarias'

export const DEFAULT_TAX_REGION: TaxRegion = 'peninsula'

/** Provincias con IGIC (ISO 3166-2:ES). No incluye CE ni ML. */
export const CANARY_PROVINCE_CODES: readonly SpainProvinceCode[] = ['GC', 'TF']

export type TaxTreatment = {
  readonly region: TaxRegion
  /** Nombre del impuesto tal y como se le enseña al alumno. */
  readonly taxName: 'IVA' | 'IGIC'
  readonly ratePercent: number
  /** total = base * multiplier */
  readonly multiplier: number
  /** Clave del impuesto en Holded. Se manda en item.taxes, nunca en item.tax. */
  readonly holdedTaxKey: string
}

export const TAX_TREATMENTS: Readonly<Record<TaxRegion, TaxTreatment>> = {
  peninsula: {
    region: 'peninsula',
    taxName: 'IVA',
    ratePercent: 21,
    multiplier: 1.21,
    holdedTaxKey: 's_iva_21',
  },
  canarias: {
    region: 'canarias',
    taxName: 'IGIC',
    ratePercent: 7,
    multiplier: 1.07,
    holdedTaxKey: 's_igic_7',
  },
}

export const getTaxTreatment = (
  region: TaxRegion | null | undefined
): TaxTreatment => TAX_TREATMENTS[region ?? DEFAULT_TAX_REGION]

export const isTaxRegion = (value: unknown): value is TaxRegion =>
  value === 'peninsula' || value === 'canarias'

/** Lee una región de la metadata de Stripe. Devuelve null si no es válida. */
export const parseTaxRegion = (
  value: string | null | undefined
): TaxRegion | null => {
  if (!value) return null
  const normalized = value.trim().toLowerCase()
  return isTaxRegion(normalized) ? normalized : null
}

/**
 * Región a partir de una provincia, aceptando código ('GC') o nombre
 * ('Las Palmas'). Devuelve null si la provincia no se reconoce, para que quien
 * llame decida el valor por defecto de forma explícita.
 */
export const resolveTaxRegionFromProvince = (
  province: string | null | undefined
): TaxRegion | null => {
  const code = toSpainProvinceCode(province)
  if (!code) return null
  return CANARY_PROVINCE_CODES.includes(code) ? 'canarias' : 'peninsula'
}

export const isCanaryProvince = (
  province: string | null | undefined
): boolean => resolveTaxRegionFromProvince(province) === 'canarias'

/** Claves de la metadata del customer de Stripe. Contrato con el backend. */
export const TAX_REGION_METADATA_KEY = 'tax_region'
export const TAX_PROVINCE_METADATA_KEY = 'tax_province'

/**
 * Metadata de residencia fiscal del customer a partir de la provincia
 * declarada: tax_province y tax_region SIEMPRE juntas y coherentes (la
 * facturación toma el impuesto de una y la dirección de Holded de la otra).
 * Vacía si la provincia no se reconoce, para conservar lo que hubiera.
 */
export const buildTaxResidenceMetadata = (
  province: string | null | undefined
): Partial<Record<typeof TAX_PROVINCE_METADATA_KEY | typeof TAX_REGION_METADATA_KEY, string>> => {
  const code = toSpainProvinceCode(province)
  if (!code) return {}
  return {
    [TAX_PROVINCE_METADATA_KEY]: code,
    [TAX_REGION_METADATA_KEY]: CANARY_PROVINCE_CODES.includes(code) ? 'canarias' : 'peninsula',
  }
}

/**
 * Tipo de operación, que decide DÓNDE se grava (Ley del IVA, arts. 68-70):
 *
 *   goods    -> entrega de bienes en tienda: se grava donde se entrega. Todas
 *               las tiendas están en la península, así que siempre IVA, aunque
 *               el comprador resida en Canarias.
 *   services -> suscripciones y cursos: se grava donde reside el cliente
 *               (tax_region del customer).
 *
 * Va en la metadata de la FACTURA de Stripe, no del customer: es un dato de la
 * venta, no de quién compra. Sin ella se trata como servicio, que es lo que
 * vende el checkout web.
 */
export type SupplyType = 'goods' | 'services'

export const SUPPLY_TYPE_METADATA_KEY = 'supply_type'

/** Lee el tipo de operación de la metadata de Stripe. null si no es válido. */
export const parseSupplyType = (
  value: string | null | undefined
): SupplyType | null => {
  const normalized = value?.trim().toLowerCase()
  return normalized === 'goods' || normalized === 'services' ? normalized : null
}

export type TaxAmounts = {
  /** Base imponible, redondeada a 2 decimales para enseñarla. */
  readonly subtotal: number
  /** Cuota del impuesto. subtotal + tax == total exactamente. */
  readonly tax: number
  readonly total: number
  readonly treatment: TaxTreatment
}

/**
 * Parte un importe que YA lleva el impuesto incluido. Mantiene la semántica
 * que tenía el checkout: se redondea la base y la cuota es la diferencia, de
 * modo que las dos líneas siempre suman el total que se cobra.
 */
export const splitTaxIncludedAmount = (
  total: number,
  region: TaxRegion | null | undefined
): TaxAmounts => {
  const treatment = getTaxTreatment(region)
  if (!total) {
    return { subtotal: 0, tax: 0, total: 0, treatment }
  }
  const subtotal = parseFloat((total / treatment.multiplier).toFixed(2))
  return {
    subtotal,
    tax: parseFloat((total - subtotal).toFixed(2)),
    total,
    treatment,
  }
}

/**
 * Base imponible SIN redondear, para la factura. Holded recalcula el total a
 * partir de la base, así que redondear aquí descuadra céntimos en facturas de
 * varias líneas (mismo motivo que el comentario de stripe-invoice-item.mapper).
 */
export const taxBaseFromTotal = (
  totalWithTax: number,
  region: TaxRegion | null | undefined
): number => totalWithTax / getTaxTreatment(region).multiplier
